import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogTitle,
  Input,
} from '@mlm/ui';
import {
  getActivationTerms,
  getMe,
  getTenantConfig,
  getTerms,
  listMyCodes,
  listMyProductCodes,
  listMySlots,
  previewRedeem,
  redeemKit,
  redeemProductCode,
  transferCodeToDownline,
  upgradeSlot,
  type KitCode,
  type ProductCode,
  type RedeemPreview,
  type RedeemResult,
  type Slot,
  type UpgradeSlotResult,
} from '../lib/api';
import { fmtCurrency } from '../lib/format';
import { prettyRedeemError } from '../lib/redeem-errors';
import { useBrand } from '../storefront/lib/hooks';
import { RedeemForDownlineDialog } from '../components/redeem-for-downline-dialog';

/** Display label for a kit code's / preview's slot type. */
function slotTypeLabel(t: 'paid' | 'free' | 'cd' | 'half_cd' | undefined): string {
  switch (t) {
    case 'free':
      return 'Free slot';
    case 'cd':
      return 'CD slot';
    case 'half_cd':
      return 'Half-CD slot';
    default:
      return 'Paid slot';
  }
}

/**
 * Code Vault page. Lists every kit code the member owns, with reveal/hide on
 * the PIN (it's stored plaintext server-side but we still don't blurt it on
 * screen unprompted). "Redeem this" opens an inline form to activate the
 * member's own slot via Code+PIN; the form lets them override sponsor and
 * pick a placement (anchor + L/R) when they already have slot(s).
 */
export function CodeVaultPage() {
  const brand = useBrand();
  const queryClient = useQueryClient();
  const codesQuery = useQuery({
    queryKey: ['vault'],
    queryFn: () => listMyCodes(),
  });
  const slotsQuery = useQuery({
    queryKey: ['my-slots'],
    queryFn: listMySlots,
  });
  const tenantQuery = useQuery({
    queryKey: ['tenant-config'],
    queryFn: getTenantConfig,
  });
  const transferEnabled = tenantQuery.data?.kitCodesDownlineTransfer === true;

  const [redeemingCodeId, setRedeemingCodeId] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<RedeemResult | null>(null);
  const [upgradingCodeId, setUpgradingCodeId] = useState<string | null>(null);
  const [upgradeReceipt, setUpgradeReceipt] = useState<UpgradeSlotResult | null>(null);
  const [transferringCodeId, setTransferringCodeId] = useState<string | null>(null);
  const [transferDone, setTransferDone] = useState(false);
  const [downlineRedeemOpen, setDownlineRedeemOpen] = useState(false);
  const [downlineRedeemDone, setDownlineRedeemDone] = useState(false);

  const available = codesQuery.data?.filter((c) => !c.redeemedAt) ?? [];
  const redeemed = codesQuery.data?.filter((c) => c.redeemedAt) ?? [];

  // F6: PV-carrying product codes. Only surfaced when the member actually has
  // some (tenants not using Product Code Generation see nothing).
  const productCodesQuery = useQuery({
    queryKey: ['product-codes'],
    queryFn: listMyProductCodes,
  });
  const productCodes = productCodesQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <h1 className="text-h1">Code Vault</h1>
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground">
            {available.length} available · {redeemed.length} redeemed
          </p>
          {available.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => setDownlineRedeemOpen(true)}>
              Use a code for an affiliate
            </Button>
          )}
        </div>
      </div>

      <RedeemForDownlineDialog
        open={downlineRedeemOpen}
        onClose={() => setDownlineRedeemOpen(false)}
        onSuccess={() => {
          setDownlineRedeemOpen(false);
          setDownlineRedeemDone(true);
        }}
      />

      <Dialog open={downlineRedeemDone} onOpenChange={(o) => !o && setDownlineRedeemDone(false)}>
        <DialogContent className="max-w-sm text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
            <CheckCircle2 className="h-7 w-7 text-success" />
          </div>
          <DialogTitle className="mt-2 text-center text-h2">Slot created</DialogTitle>
          <p className="text-sm text-muted-foreground">
            A new slot was created for your affiliate, sponsored by you.
          </p>
          <Button onClick={() => setDownlineRedeemDone(false)} className="mt-4 w-full">
            Done
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="max-w-sm text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
            <CheckCircle2 className="h-7 w-7 text-success" />
          </div>
          <DialogTitle className="mt-2 text-center text-h2">Slot activated</DialogTitle>
          {receipt?.successMessage && (
            <p className="whitespace-pre-wrap text-sm font-medium text-success">
              {receipt.successMessage}
            </p>
          )}
          <div className="mt-1 space-y-1 rounded-md bg-muted/50 p-3 text-left text-sm">
            <div>
              Slot code: <span className="font-mono">{receipt?.slotCode}</span>
            </div>
            <div>
              Membership: <span className="font-mono">{receipt?.membershipCode}</span>
            </div>
            <div>
              Placed under: <span className="font-mono">{receipt?.binaryParentSlotId ?? '—'}</span>
              {receipt?.binaryPosition && ` (${receipt.binaryPosition})`}
            </div>
          </div>
          <Button onClick={() => setReceipt(null)} className="mt-4 w-full">
            Done
          </Button>
        </DialogContent>
      </Dialog>

      {upgradeReceipt && (
        <Card className="border-primary/30 bg-primary/5">
          <CardHeader>
            <CardTitle>Slot upgraded</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <div>
              Now:{' '}
              <span className="font-mono">
                {upgradeReceipt.afterMembershipCode} · {upgradeReceipt.afterSlotType}
              </span>
            </div>
            {upgradeReceipt.becamePaid && (
              <div className="text-success">This slot can now earn commissions.</div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUpgradeReceipt(null)}
              className="mt-2"
            >
              Dismiss
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={transferDone} onOpenChange={(o) => !o && setTransferDone(false)}>
        <DialogContent className="max-w-sm text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
            <CheckCircle2 className="h-7 w-7 text-success" />
          </div>
          <DialogTitle className="mt-2 text-center text-h2">Code transferred</DialogTitle>
          <p className="text-sm text-muted-foreground">
            The code has moved to your affiliate's Code Vault and is no longer in yours.
          </p>
          <Button onClick={() => setTransferDone(false)} className="mt-4 w-full">
            Done
          </Button>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Available codes</CardTitle>
        </CardHeader>
        <CardContent>
          {codesQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : available.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No unredeemed codes. Buy a {brand.membershipKitLabel} to get one.
            </p>
          ) : (
            <ul className="space-y-3">
              {available.map((c) => (
                <li key={c.id} className="border rounded-md">
                  {redeemingCodeId === c.id ? (
                    <RedeemForm
                      code={c}
                      slots={slotsQuery.data ?? []}
                      onCancel={() => setRedeemingCodeId(null)}
                      onSuccess={(r) => {
                        setReceipt(r);
                        setRedeemingCodeId(null);
                        queryClient.invalidateQueries({ queryKey: ['vault'] });
                        queryClient.invalidateQueries({ queryKey: ['my-slots'] });
                        queryClient.invalidateQueries({ queryKey: ['onboarding'] });
                        // Sponsor editability depends on slot count — refresh /me.
                        queryClient.invalidateQueries({ queryKey: ['me'] });
                      }}
                    />
                  ) : upgradingCodeId === c.id ? (
                    <UpgradeForm
                      code={c}
                      slots={slotsQuery.data ?? []}
                      onCancel={() => setUpgradingCodeId(null)}
                      onSuccess={(r) => {
                        setUpgradeReceipt(r);
                        setUpgradingCodeId(null);
                        queryClient.invalidateQueries({ queryKey: ['vault'] });
                        queryClient.invalidateQueries({ queryKey: ['my-slots'] });
                        queryClient.invalidateQueries({ queryKey: ['onboarding'] });
                      }}
                    />
                  ) : transferringCodeId === c.id ? (
                    <TransferForm
                      code={c}
                      onCancel={() => setTransferringCodeId(null)}
                      onSuccess={() => {
                        setTransferringCodeId(null);
                        setTransferDone(true);
                        queryClient.invalidateQueries({ queryKey: ['vault'] });
                      }}
                    />
                  ) : (
                    <CodeRow
                      code={c}
                      // Downline transfer depends only on the tenant flag, not
                      // the `transferable` gift-card snapshot (which governs open
                      // redemption). See transferCodeToDownline.
                      canTransfer={transferEnabled}
                      onRedeem={() => setRedeemingCodeId(c.id)}
                      onUpgrade={() => setUpgradingCodeId(c.id)}
                      onTransfer={() => setTransferringCodeId(c.id)}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {productCodes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Product codes (PV)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-3 text-sm text-muted-foreground">
              These codes hold the points (PV) from your purchases. Redeem a code to release its PV
              into your account.
            </p>
            <ul className="space-y-3">
              {productCodes.map((pc) => (
                <li key={pc.id} className="border rounded-md p-3">
                  <ProductCodeRow
                    code={pc}
                    onRedeemed={() => {
                      queryClient.invalidateQueries({ queryKey: ['product-codes'] });
                    }}
                  />
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {redeemed.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Redeemed</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {redeemed.map((c) => (
                <li key={c.id} className="flex items-center justify-between">
                  <div>
                    <div className="font-mono">{c.code}</div>
                    <div className="text-xs text-muted-foreground">{c.productName}</div>
                    {c.purchasedAt && (
                      <div className="text-xs text-muted-foreground">
                        Purchased {new Date(c.purchasedAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {c.redeemedAt && new Date(c.redeemedAt).toLocaleDateString()}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CodeRow({
  code,
  canTransfer,
  onRedeem,
  onUpgrade,
  onTransfer,
}: {
  code: KitCode;
  canTransfer: boolean;
  onRedeem: () => void;
  onUpgrade: () => void;
  onTransfer: () => void;
}) {
  const [showPin, setShowPin] = useState(false);
  const expiresSoon =
    code.expiresAt && new Date(code.expiresAt).getTime() - Date.now() < 30 * 24 * 3600 * 1000;
  const mode = code.usageMode ?? 'activation';
  const canActivate = mode === 'activation' || mode === 'both';
  const canUpgrade = mode === 'upgrade' || mode === 'both';
  const modeLabel =
    mode === 'both' ? 'Activate or upgrade' : mode === 'upgrade' ? 'Upgrade only' : 'Activation';
  return (
    <div className="p-3 space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="font-medium">{code.productName}</div>
          <div className="text-xs text-muted-foreground">
            {code.productSku} · {slotTypeLabel(code.slotType)} · {modeLabel}
            {(code.slotType === 'cd' || code.slotType === 'half_cd') && code.debtAmount ? (
              <> · Debt {fmtCurrency(code.debtAmount)}</>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          {canActivate && (
            <Button size="sm" onClick={onRedeem}>
              Redeem
            </Button>
          )}
          {canUpgrade && (
            <Button size="sm" variant={canActivate ? 'outline' : 'default'} onClick={onUpgrade}>
              Upgrade
            </Button>
          )}
          {canTransfer && (
            <Button size="sm" variant="outline" onClick={onTransfer}>
              Transfer
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <div className="text-xs text-muted-foreground">Code</div>
          <div className="font-mono">{code.code}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">PIN</div>
          <div className="font-mono">
            {showPin ? code.pin : '••••••'}{' '}
            <button
              className="text-xs underline text-muted-foreground"
              onClick={() => setShowPin((s) => !s)}
            >
              {showPin ? 'hide' : 'reveal'}
            </button>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span>{code.transferable ? 'Transferable' : 'Not transferable'}</span>
        {code.purchasedAt && <span>Purchased {new Date(code.purchasedAt).toLocaleString()}</span>}
        <span>Issued {new Date(code.createdAt).toLocaleDateString()}</span>
        {code.expiresAt && (
          <span className={expiresSoon ? 'text-destructive' : ''}>
            Expires {new Date(code.expiresAt).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
}

const TRANSFER_ERROR_MESSAGES: Record<string, string> = {
  transfer_not_enabled: 'Code transfers are not enabled.',
  code_not_in_your_vault: 'This code is no longer in your vault.',
  already_redeemed: 'This code has already been redeemed.',
  not_transferable: 'This code is not transferable.',
  code_expired: 'This code has expired.',
  recipient_not_found: 'No member matches that email.',
  recipient_username_mismatch: 'That username does not match the account for that email.',
  recipient_inactive: 'That member’s account is not active.',
  cannot_transfer_to_self: 'You can’t transfer a code to yourself.',
  recipient_not_in_downline: 'That member is not one of your affiliates.',
};

function TransferForm({
  code,
  onCancel,
  onSuccess,
}: {
  code: KitCode;
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const [recipientUsername, setRecipientUsername] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Pure visibility gate: the field only appears where usernames are collected.
  // It is never required — matching is by email, and plenty of accounts on a
  // username tenant still have none (legacy imports).
  const signupCfgQ = useQuery({ queryKey: ['terms'], queryFn: getTerms });
  const usesUsernames = !!signupCfgQ.data?.requireUsernameOnSignup;

  const transfer = useMutation({
    meta: { successMessage: 'Code transferred.' },
    mutationFn: () =>
      transferCodeToDownline({
        codeId: code.id,
        recipientUsername: recipientUsername.trim() || undefined,
        recipientEmail: recipientEmail.trim(),
      }),
    onSuccess,
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string } } }).response?.data;
      setError(data?.error ?? 'transfer_failed');
    },
  });

  const canSubmit = recipientEmail.trim() !== '';

  return (
    <form
      className="p-3 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (canSubmit) transfer.mutate();
      }}
    >
      <div>
        <div className="font-medium">Transfer code to an affiliate</div>
        <div className="text-xs text-muted-foreground">
          {code.productName} · {code.code}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {usesUsernames
          ? 'Enter the recipient’s email. Adding their username double-checks the match, but it’s optional and only enforced when their account has one.'
          : 'Enter the recipient’s email. It must match one of your affiliates.'}{' '}
        The code will move out of your vault into theirs.
      </p>
      <div className="space-y-2">
        {usesUsernames && (
          <div>
            <label className="text-xs text-muted-foreground" htmlFor="recipient-username">
              Recipient username (optional)
            </label>
            <Input
              id="recipient-username"
              value={recipientUsername}
              onChange={(e) => setRecipientUsername(e.target.value)}
              autoComplete="off"
            />
          </div>
        )}
        <div>
          <label className="text-xs text-muted-foreground" htmlFor="recipient-email">
            Recipient email
          </label>
          <Input
            id="recipient-email"
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            autoComplete="off"
          />
        </div>
      </div>
      {error && (
        <p className="text-sm text-destructive">
          {TRANSFER_ERROR_MESSAGES[error] ?? 'Transfer failed. Please try again.'}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!canSubmit || transfer.isPending}>
          {transfer.isPending ? 'Transferring…' : 'Transfer code'}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function RedeemForm({
  code,
  slots,
  onCancel,
  onSuccess,
}: {
  code: KitCode;
  slots: Slot[];
  onCancel: () => void;
  onSuccess: (r: RedeemResult) => void;
}) {
  const activeSlots = slots.filter((s) => s.status === 'active');
  const [pin, setPin] = useState(code.pin); // pre-filled — buyer can see PIN
  const [sponsorCode, setSponsorCode] = useState('');
  const [anchorSlotId, setAnchorSlotId] = useState<string>(activeSlots[0]?.id ?? '');
  const [side, setSide] = useState<'L' | 'R'>('L');
  // Placement is opt-in: by default the new slot is activated but left unplaced
  // (the member places it from the genealogy view whenever they're ready).
  const [placeNow, setPlaceNow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Two-step flow: fill form → preview the sponsor → confirm + activate.
  const [preview, setPreview] = useState<RedeemPreview | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const termsQ = useQuery({ queryKey: ['activation-terms'], queryFn: getActivationTerms });

  // Pre-fill the sponsor field with the slot code the backend would resolve
  // by default — for replicated-link signups this surfaces the sponsor the
  // member arrived under, instead of hiding it behind a "Leave blank" hint.
  // The member can still edit it to override.
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });
  const referredViaLink = meQ.data?.referredViaLink ?? false;
  // First slot = the member has no active slot yet. Drives the lock + pre-fill:
  // a first-slot link member is locked to their registration sponsor; a 2nd+
  // slot is pre-filled with the server's suggestion (own first slot when
  // self-stacking is on) but stays editable.
  const isFirstSlot = activeSlots.length === 0;
  // Manual placement UI is opt-in per tenant (default off). When off, the
  // checkbox is hidden and placement falls through to the server default
  // (auto-spillover or unplaced, per `auto_place_slots`).
  const tenantQ = useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig });
  const manualPlacement = tenantQ.data?.manualPlacementOnRedeem === true;
  // Admin authoritative sponsor override: the field is pre-filled + locked, and
  // HIDDEN entirely when the tenant turned `show_sponsor_on_redeem` off (stealth
  // — the member can't see they were overridden). Server enforces the override
  // regardless of what the field shows.
  const sponsorOverridden = meQ.data?.sponsorOverridden ?? false;
  const showSponsorOnRedeem = tenantQ.data?.showSponsorOnRedeem !== false;
  const hideSponsorField = sponsorOverridden && !showSponsorOnRedeem;
  // Server-authoritative editability (per the tenant editability toggles).
  // Undefined on an older API = editable. An admin override always locks too.
  const sponsorEditable = meQ.data?.sponsorEditable !== false;
  const lockSponsor = !sponsorEditable || sponsorOverridden;
  useEffect(() => {
    // Pre-fill the sponsor the backend would resolve by default:
    //  - first-slot link members → their registration sponsor (field locked).
    //  - subsequent slots → their own first slot / signup sponsor (editable).
    //  - overridden members → the override slot (locked).
    // First-slot walk-ins start blank so the "You didn't input sponsor code."
    // prompt shows until they type one (an empty submit still safely defaults
    // server-side to their registration / super-admin sponsor).
    const fromMe = meQ.data?.defaultSponsorSlotCode ?? null;
    const shouldPrefill = referredViaLink || !isFirstSlot || sponsorOverridden;
    if (shouldPrefill && fromMe && sponsorCode === '') setSponsorCode(fromMe);
    // Run once when /me lands — we deliberately ignore later changes so we
    // don't clobber a value the member has typed themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meQ.data?.defaultSponsorSlotCode, referredViaLink, isFirstSlot, sponsorOverridden]);

  const onError = (err: unknown) => {
    const data = (err as { response?: { data?: { error?: string } } }).response?.data;
    setError(data?.error ?? 'redeem_failed');
  };

  const previewMut = useMutation({
    mutationFn: previewRedeem,
    onSuccess: (p) => setPreview(p),
    onError,
  });

  const redeem = useMutation({
    meta: { successMessage: 'Slot activated.' },
    mutationFn: redeemKit,
    onSuccess,
    onError,
  });

  // Step 1 → resolve the sponsor for confirmation.
  function onPreview(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    previewMut.mutate({ code: code.code, pin, sponsorCode: sponsorCode || undefined });
  }

  // Step 2 → activate, carrying the placement + terms acceptance.
  function onConfirm() {
    setError(null);
    redeem.mutate({
      code: code.code,
      pin,
      sponsorCode: sponsorCode || undefined,
      anchorSlotId: placeNow && activeSlots.length > 0 ? anchorSlotId : undefined,
      side: placeNow && activeSlots.length > 0 ? side : undefined,
      termsAccepted: termsQ.data?.required ? termsAccepted : undefined,
      termsVersion: termsQ.data?.required ? termsQ.data.version : undefined,
    });
  }

  // ── Step 2: sponsor confirmation + activation terms ──
  if (preview) {
    const needsTerms = !!termsQ.data?.required;
    return (
      <div className="p-3 space-y-3 bg-muted/30">
        <div className="text-sm font-medium">Confirm activation</div>
        <Card>
          <CardContent className="p-3 space-y-1 text-sm">
            <div className="text-xs text-muted-foreground">You will be placed under</div>
            <div className="font-medium">
              {preview.sponsor.ownerFullName ?? preview.sponsor.ownerEmail}
            </div>
            <div className="text-xs text-muted-foreground">
              Sponsor slot <span className="font-mono">{preview.sponsor.slotCode}</span> ·{' '}
              {preview.sponsor.membershipCode}
            </div>
            <div className="text-xs text-muted-foreground pt-1">
              Activating: <span className="font-medium">{preview.membershipCode}</span> (
              {slotTypeLabel(preview.slotType).toLowerCase()})
            </div>
            {(preview.slotType === 'cd' || preview.slotType === 'half_cd') && preview.debtAmount ? (
              <div className="rounded-md border border-warning/40 bg-warning/10 p-2 text-xs">
                This slot starts with a <strong>{fmtCurrency(preview.debtAmount)} debt</strong>.
                {preview.slotType === 'cd'
                  ? ' All of its earnings pay the debt off first; once settled, it becomes a regular paid slot.'
                  : ' Part of its earnings pay the debt off first (per the plans your admin configured); once settled, it becomes a regular paid slot.'}
              </div>
            ) : null}
          </CardContent>
        </Card>

        {needsTerms && (
          <div className="space-y-2">
            {termsQ.data?.text && (
              <div className="max-h-40 overflow-y-auto rounded-md border bg-background p-2 text-xs whitespace-pre-wrap">
                {termsQ.data.text}
              </div>
            )}
            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                I understand this activation <strong>cannot be changed</strong> once done, and I
                agree to the terms above.
              </span>
            </label>
          </div>
        )}

        {error && <p className="text-xs text-destructive">{prettyRedeemError(error)}</p>}
        <div className="flex gap-2">
          <Button
            type="button"
            disabled={redeem.isPending || (needsTerms && !termsAccepted)}
            onClick={onConfirm}
            className="flex-1"
          >
            {redeem.isPending ? 'Activating…' : 'Confirm & activate'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setPreview(null);
              setError(null);
            }}
          >
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onPreview} className="p-3 space-y-3 bg-muted/30">
      <div className="text-sm font-medium">Redeem {code.code}</div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-muted-foreground" htmlFor="pin">
            PIN
          </label>
          <Input id="pin" value={pin} onChange={(e) => setPin(e.target.value)} required />
        </div>
        {hideSponsorField ? null : (
          <div>
            <label className="text-xs text-muted-foreground" htmlFor="sponsor">
              Affiliate Code
            </label>
            <Input
              id="sponsor"
              placeholder="Affiliate or slot code"
              value={sponsorCode}
              // Locked when the tenant disabled editing for this activation domain
              // (link / extra-slots / self-stack), OR when an admin set an
              // authoritative sponsor override. Otherwise any referral/slot code.
              readOnly={lockSponsor}
              className={lockSponsor ? 'bg-muted' : undefined}
              onChange={(e) => setSponsorCode(e.target.value.toUpperCase())}
            />
            {sponsorOverridden ? (
              <p className="mt-1 text-[11px] text-muted-foreground">Set by your administrator.</p>
            ) : lockSponsor ? (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Locked to your sponsor for this activation.
              </p>
            ) : referredViaLink && isFirstSlot ? (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Pre-filled from your sponsor's link — you can change it.
              </p>
            ) : !sponsorCode ? (
              <p className="mt-1 text-[11px] text-amber-600">You didn't input an affiliate code.</p>
            ) : (
              !isFirstSlot && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Suggested sponsor — change it to place this slot elsewhere.
                </p>
              )
            )}
          </div>
        )}
      </div>
      {activeSlots.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          First slot — it activates now; place it in your tree later from the genealogy view.
        </p>
      ) : !manualPlacement ? (
        <p className="text-xs text-muted-foreground">
          It activates now; placement follows your plan's settings — manage it from the genealogy
          view.
        </p>
      ) : (
        <div className="space-y-2">
          {/* Placement is opt-in. Left off, the slot activates unplaced and can
              be placed later from the genealogy view. */}
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={placeNow}
              onChange={(e) => setPlaceNow(e.target.checked)}
            />
            <span>Place in my binary tree now (otherwise place it later)</span>
          </label>
          {placeNow && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground" htmlFor="anchor">
                  Anchor slot
                </label>
                <select
                  id="anchor"
                  value={anchorSlotId}
                  onChange={(e) => setAnchorSlotId(e.target.value)}
                  className="w-full h-9 px-2 rounded-md border bg-background text-sm"
                >
                  {activeSlots.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.slotCode}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Side</label>
                <div className="flex gap-2">
                  {(['L', 'R'] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setSide(p)}
                      className={`flex-1 py-1.5 rounded-md border text-sm ${
                        side === p
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'hover:bg-accent'
                      }`}
                    >
                      {p === 'L' ? 'Left' : 'Right'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {error && <p className="text-xs text-destructive">{prettyRedeemError(error)}</p>}
      <div className="flex gap-2">
        <Button type="submit" disabled={previewMut.isPending} className="flex-1">
          {previewMut.isPending ? 'Checking…' : 'Continue'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function UpgradeForm({
  code,
  slots,
  onCancel,
  onSuccess,
}: {
  code: KitCode;
  slots: Slot[];
  onCancel: () => void;
  onSuccess: (r: UpgradeSlotResult) => void;
}) {
  const activeSlots = slots.filter((s) => s.status === 'active');
  const [pin, setPin] = useState(code.pin);
  const [slotId, setSlotId] = useState<string>(activeSlots[0]?.id ?? '');
  const [error, setError] = useState<string | null>(null);

  const upgrade = useMutation({
    meta: { successMessage: 'Slot upgraded.' },
    mutationFn: () => upgradeSlot(slotId, { code: code.code, pin }),
    onSuccess,
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string } } }).response?.data;
      setError(prettyUpgradeError(data?.error));
    },
  });

  if (activeSlots.length === 0) {
    return (
      <div className="space-y-2 bg-muted/30 p-3 text-sm">
        <p className="text-muted-foreground">
          You have no active slot to upgrade. Activate a slot first, then use this code to upgrade
          it.
        </p>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Close
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        upgrade.mutate();
      }}
      className="space-y-3 bg-muted/30 p-3"
    >
      <div className="text-sm font-medium">Upgrade a slot with {code.code}</div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-muted-foreground" htmlFor="upgrade-slot">
            Slot to upgrade
          </label>
          <select
            id="upgrade-slot"
            value={slotId}
            onChange={(e) => setSlotId(e.target.value)}
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          >
            {activeSlots.map((s) => (
              <option key={s.id} value={s.id}>
                {s.slotCode}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground" htmlFor="upgrade-pin">
            PIN
          </label>
          <Input id="upgrade-pin" value={pin} onChange={(e) => setPin(e.target.value)} required />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Upgrades to a higher membership only. A free slot becomes an earner once upgraded to paid.
      </p>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button type="submit" disabled={upgrade.isPending} className="flex-1">
          {upgrade.isPending ? 'Upgrading…' : 'Upgrade slot'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function prettyUpgradeError(codeStr?: string): string {
  switch (codeStr) {
    case 'upgrade_not_higher':
      return 'This code is not for a higher membership than the slot already has.';
    case 'code_not_for_upgrade':
      return 'This code can only activate a new slot, not upgrade one.';
    case 'cannot_downgrade_to_free':
      return 'A paid slot cannot be downgraded to free.';
    case 'membership_hierarchy_not_configured':
      return 'Membership ranking is not configured. Contact your administrator.';
    case 'already_redeemed':
      return 'This code has already been used.';
    case 'code_expired':
      return 'This code has expired.';
    case 'invalid_pin':
      return 'Incorrect PIN.';
    case 'slot_not_active':
      return 'That slot is not active.';
    default:
      return 'Upgrade failed. Please try again.';
  }
}

function ProductCodeRow({ code, onRedeemed }: { code: ProductCode; onRedeemed: () => void }) {
  const redeemed = !!code.redeemedAt;
  // The PIN travels with the code in the vault payload, so redemption is a
  // single click — no manual PIN entry. The server still verifies it.
  const redeem = useMutation({
    mutationFn: () => redeemProductCode(code.code, code.pin),
    meta: { successMessage: 'PV released to your account.' },
    onSuccess: () => {
      onRedeemed();
    },
  });
  const errorCode = (redeem.error as { response?: { data?: { error?: string } } } | null)?.response
    ?.data?.error;

  const moreCount = code.itemCount > 1 ? code.itemCount - 1 : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">
            {code.primaryProductName ?? '—'}
            {moreCount > 0 && (
              <span className="font-normal text-muted-foreground"> +{moreCount} more</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">{Number(code.pv).toLocaleString()} PV</div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
            <span>
              Code <span className="font-mono">{code.code}</span>
            </span>
            {code.orderCode && (
              <span>
                Trans# <span className="font-mono">{code.orderCode}</span>
              </span>
            )}
            <span>
              Purchased{' '}
              <span className="font-mono">
                {new Date(code.purchasedAt ?? code.createdAt).toLocaleString()}
              </span>
            </span>
          </div>
        </div>
        {redeemed ? (
          <span className="shrink-0 text-xs text-muted-foreground">
            Redeemed {code.redeemedAt && new Date(code.redeemedAt).toLocaleDateString()}
          </span>
        ) : (
          <Button size="sm" disabled={redeem.isPending} onClick={() => redeem.mutate()}>
            {redeem.isPending ? 'Redeeming…' : 'Redeem for PV'}
          </Button>
        )}
      </div>
      {redeem.isError && (
        <p className="text-xs text-destructive">
          {errorCode === 'invalid_pin'
            ? 'Incorrect PIN.'
            : errorCode === 'already_redeemed'
              ? 'This code has already been redeemed.'
              : 'Redeem failed. Please try again.'}
        </p>
      )}
    </div>
  );
}
